import axios from 'axios';
import { getToken, clearToken } from '../utils/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Create axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
});

// Request interceptor to add token
api.interceptors.request.use(
    (config) => {
        const token = getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        console.log('Request:', config.method.toUpperCase(), config.url, 'Token:', token ? 'Present' : 'Missing');
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle errors
api.interceptors.response.use(
    (response) => {
        console.log('Response:', response.config.method.toUpperCase(), response.config.url, response.status);
        return response;
    },
    (error) => {
        console.error('API Error:', error.response?.status, error.response?.data);
        
        if (error.response && error.response.status === 401) {
            console.log('401 Unauthorized - Clearing token and redirecting to login');
            clearToken();
            window.location.href = '/';
        }
        
        return Promise.reject(error);
    }
);

// API endpoints
export const fetchSchema = () => api.get('/schema');
export const fetchDocs = () => api.get('/docs');
export const fetchEvents = () => api.get('/events');
export const fetchUsers = () => api.get('/users');
export const createUser = (username, password, role) => 
    api.post('/users', { username, password, role });
export const deleteUser = (userId) => api.delete(`/users/${userId}`);

// Login uses a separate axios instance since it shouldn't have the auth header
export const loginUser = (username, password) => 
    axios.post(`${API_BASE_URL}/login`, { username, password });

// Discovery endpoints
export const getTables = (search) => {
    const params = search ? { search } : {};
    return api.get('/discovery/tables', { params });
};

export const updateTableDescription = (tableName, description) => 
    api.put(`/discovery/tables/${tableName}/description`, { description });

export const getRelationships = () => api.get('/discovery/relationships');

export const runDiscovery = () => api.post('/discovery/run-discovery');

export const getDiscoveryStatus = () => api.get('/discovery/status');

// Feature endpoints
export const getFeatures = () => api.get('/api/features');
export const getFeatureImpact = (featureId) => api.get(`/api/features/${featureId}/impact`);

// Schema/Table endpoints
export const getTableDetails = (tableName) => api.get(`/table/${tableName}`);
export const getTableRelationships = (tableName) => api.get(`/table/${tableName}/relationships`);

// Auth utilities (if needed by other components)
export const getAuth = () => {
    const token = getToken();
    const user = localStorage.getItem('user');
    return {
        isAuthenticated: !!token,
        user: user ? JSON.parse(user) : null
    };
};

export const logout = () => {
    clearToken();
    localStorage.removeItem('user');
    window.location.href = '/login';
};

// Ecosystem endpoints (if you need them)
export const getEcosystemEvents = () => api.get('/ecosystem/events');
export const getEcosystemTables = () => api.get('/ecosystem/tables');
export const getEventsByTable = (tableName) => api.get(`/ecosystem/events/by-table/${tableName}`);
export const getEntityRelations = () => api.get('/ecosystem/entity-relations');
export const syncApp = (appData) => api.post('/ecosystem/sync-app', appData);

export default api;

// Postman endpoints
export const getCollections = () => api.get('/postman/collections');
export const getCollectionEndpoints = (collectionId, params) => 
    api.get(`/postman/collections/${collectionId}/endpoints`, { params });
export const importCollection = (formData) => 
    api.post('/postman/import-collection', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
export const generateWebhookTest = (event, url) => 
    api.post('/postman/generate-webhook-test', { event_type: event, webhook_url: url });
export const getEndpointToEvents = () => api.get('/postman/endpoint-to-events');

// Postman connection endpoints
export const connectPostman = (apiKey) => 
    api.post('/postman/connect-postman', null, { params: { api_key: apiKey } });

export const getPostmanWorkspaces = () => 
    api.get('/postman/postman-workspaces');

export const getPostmanCollections = (workspaceId) => 
    api.get('/postman/postman-collections', { 
        params: workspaceId ? { workspace_id: workspaceId } : {} 
    });

export const syncPostmanCollection = (collectionUid) => 
    api.post(`/postman/sync-collection/${collectionUid}`);

export const getPostmanEnvironments = () => 
    api.get('/postman/postman-environments');

export const getPostmanEnvironment = (environmentUid) => 
    api.get(`/postman/postman-environment/${environmentUid}`);

export const deleteCollection = (collectionId) => 
    api.delete(`/postman/collections/${collectionId}`);

// Endpoint management
export const createEndpoint = (collectionId, endpointData) => 
    api.post(`/postman/collections/${collectionId}/endpoints`, endpointData);

export const updateEndpoint = (endpointId, endpointData) => 
    api.put(`/postman/endpoints/${endpointId}`, endpointData);

// Collections
export const createCollection = (name, description) => 
    api.post('/postman/collections/create', null, { params: { name, description } });

// Environments
export const createEnvironment = (data) => api.post('/environments', data);
export const updateEnvironment = (id, data) => api.put(`/environments/${id}`, data);

// History
export const getRequestHistory = () => api.get('/request-history');

// Test endpoint with actual HTTP request (not the mock one)
export const testEndpointActual = (config) => 
    api.post('/postman/test-endpoint-actual', config);

// Create standalone endpoint (for developers to create their own)
export const createEndpointStandalone = (endpointData) => 
    api.post('/postman/endpoints', endpointData);

const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return token ? `Bearer ${token}` : '';
};

export const testEndpoint = async (data) => {
    const response = await fetch(`${API_BASE_URL}/api/test-endpoint-actual`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': getAuthHeader()
        },
        body: JSON.stringify(data)
    });
    
    if (!response.ok) {
        throw response;
    }
    
    return { data: await response.json() };
};

export const searchEndpoints = async (query) => {
    const response = await fetch(`${API_BASE_URL}/api/endpoints/search?q=${encodeURIComponent(query)}`, {
        headers: {
            'Authorization': getAuthHeader()
        }
    });
    
    if (!response.ok) {
        throw response;
    }
    
    return { data: await response.json() };
};

export const saveEndpoint = async (data) => {
    const response = await fetch(`${API_BASE_URL}/api/endpoints`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': getAuthHeader()
        },
        body: JSON.stringify(data)
    });
    
    if (!response.ok) {
        throw response;
    }
    
    return { data: await response.json() };
};

export const getEnvironments = async () => {
    // Mock for now - implement when you have the endpoint
    return { data: [] };
};

export const saveRequestHistory = async (data) => {
    // Mock for now - implement when you have the endpoint
    return { data: { success: true } };
};

// Get current user info
export const getCurrentUser = () => api.get('/me');

// For debugging in production, add this test function
export const testAuth = async () => {
    try {
        // Test if API is reachable
        console.log('Testing API connection...');
        const healthResponse = await axios.get(`${API_BASE_URL}/health`);
        console.log('Health check:', healthResponse.data);
        
        // Test login endpoint
        console.log('Testing login endpoint...');
        const loginTest = await axios.post(`${API_BASE_URL}/login`, {
            username: 'test',
            password: 'test'
        });
        console.log('Login test response:', loginTest.data);
    } catch (error) {
        console.error('Auth test error:', error.response || error);
        if (error.response) {
            console.error('Response data:', error.response.data);
            console.error('Response status:', error.response.status);
        }
    }
};

// Debug function to check what URL is being used
export const checkApiUrl = () => {
    console.log('Current API URL:', API_BASE_URL);
    console.log('Environment:', process.env.NODE_ENV);
    console.log('Configured URL:', process.env.REACT_APP_API_URL);
};

// API Key Management Functions
export const generateApiKey = async (keyData) => {
    return api.post('/api-keys/generate', keyData);
};

export const listMyApiKeys = async () => {
    return api.get('/api-keys/my-keys');
};

export const listAllApiKeys = async () => {
    return api.get('/api-keys/all');
};

export const revokeApiKey = async (keyId) => {
    return api.delete(`/api-keys/${keyId}`);
};

export const rotateApiKey = async (keyId) => {
    return api.post(`/api-keys/${keyId}/rotate`, {});
};

// Security Scanner API Functions - FIXED VERSIONS
export const getSecurityDashboard = async () => {
    try {
        const response = await api.get('/api/security/dashboard');
        return response;
    } catch (error) {
        console.error('Error fetching security dashboard:', error);
        if (error.response && error.response.status === 404) {
            // Return default dashboard data
            return { 
                data: { 
                    summary: {
                        total: 0,
                        critical: 0,
                        high: 0,
                        medium: 0,
                        low: 0
                    },
                    archived_stats: {
                        fixed: 0,
                        false_positives: 0
                    }
                } 
            };
        }
        throw error;
    }
};

export const getVulnerabilities = async (params) => {
    return api.get('/api/security/vulnerabilities', { params });
};

export const getScanHistory = async (params = {}) => {
    try {
        const response = await api.get('/api/security/scan-history', { params });
        return response;
    } catch (error) {
        console.error('Error fetching scan history:', error);
        // Return empty data structure instead of throwing
        if (error.response && error.response.status === 404) {
            console.warn('Scan history endpoint not found, returning empty data');
            return { data: { scans: [] } };
        }
        // For other errors, you might want to handle differently
        throw error;
    }
};

export const startSecurityScan = async (scanConfig) => {
    return api.post('/api/security/scan', scanConfig);
};

// FIXED: These are the corrected security API functions
export const markVulnerabilityFixed = async (vulnId) => {
    return api.post(`/api/security/vulnerabilities/${vulnId}/mark-fixed`);
};

export const markFalsePositive = async (vulnId) => {
    // FIXED: Changed from PUT to POST to match backend
    return api.post(`/api/security/vulnerabilities/${vulnId}/false-positive`);
};

export const getAIFixSuggestion = async (vulnId) => {
    return api.get(`/api/security/vulnerabilities/${vulnId}/ai-fix`);
};

export const exportVulnerabilities = async (params) => {
    return api.get('/api/security/vulnerabilities/export', { 
        params,
        responseType: 'blob' // Important for file downloads
    });
};

// Optional: Add reopen vulnerability function
export const reopenVulnerability = async (vulnId) => {
    return api.post(`/api/security/vulnerabilities/${vulnId}/reopen`);
};

// Enhanced security API helper with better error handling
export const securityApi = {
    async markFixed(vulnId) {
        try {
            const response = await api.post(`/api/security/vulnerabilities/${vulnId}/mark-fixed`);
            return response;
        } catch (error) {
            console.error('Error marking vulnerability as fixed:', error);
            if (error.response?.status === 404) {
                console.error(`Vulnerability ${vulnId} not found`);
            }
            throw error;
        }
    },

    async markFalsePositive(vulnId) {
        try {
            const response = await api.post(`/api/security/vulnerabilities/${vulnId}/false-positive`);
            return response;
        } catch (error) {
            console.error('Error marking as false positive:', error);
            throw error;
        }
    },

    async getAIFix(vulnId, regenerate = false) {
        try {
            const response = await api.get(`/api/security/vulnerabilities/${vulnId}/ai-fix`, {
                params: { regenerate }
            });
            return response;
        } catch (error) {
            console.error('Error getting AI fix:', error);
            throw error;
        }
    }
};